import { clamp } from 'lodash-es';
import { useEffect, useRef, type FC } from 'react';
import { cx } from '../cx';
import { globalListenerHelper } from '../hooks/global-listener-helper';

interface Props {
  container: React.MutableRefObject<HTMLDivElement | null>;
  onChange?: (width: number) => void;
  onOver?: (width: number) => void;
  className?: string;
  alignLeft?: boolean;
  min?: number;
  max?: number;
  showBar?: boolean;
}

export const DragLine: FC<Props> = ({
  container,
  onChange,
  className,
  min = 100,
  max = 800,
  alignLeft = false,
  showBar = true,
  onOver,
}) => {
  const moveBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const moveBar = moveBarRef.current;
    const containerNode = container.current;

    if (!moveBar || !containerNode) return;
    let width = containerNode.clientWidth;
    let initPos = { x: 0, y: 0, width: containerNode.clientWidth };

    const handleMousemove = (event: MouseEvent) => {
      const delta = alignLeft ? event.clientX - initPos.x : initPos.x - event.clientX;
      width = clamp(initPos.width + delta, min, max);
      onChange?.(width);
    };

    const handleMouseup = () => {
      onOver?.(width);
      cleanUp();
    };

    const handleMousedown = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      document.body.classList.add('pointer-events-none');

      initPos = { x: event.clientX, y: event.clientY, width: containerNode.clientWidth };

      globalListenerHelper.addEventListener('mousemove', handleMousemove);
      globalListenerHelper.addEventListener('mouseup', handleMouseup);
    };

    const cleanUp = () => {
      document.body.classList.remove('pointer-events-none');
      globalListenerHelper.removeEventListener('mousemove', handleMousemove);
      globalListenerHelper.removeEventListener('mouseup', handleMouseup);
    };

    moveBar.addEventListener('mousedown', handleMousedown, true);

    return () => {
      cleanUp();
      moveBar.removeEventListener('mousedown', handleMousedown, true);
    };
  }, [container, max, min, onChange, alignLeft, onOver]);

  return (
    <div
      className={cx('group absolute top-0 -right-0.5 h-full w-1.5 cursor-col-resize', className)}
      ref={moveBarRef}
    >
      <div className={cx('h-full w-1 delay-100 duration-250', showBar && 'group-hover:bg-grey6')} />
    </div>
  );
};
